/**
 * 48コマスロット操作の純関数ユーティリティ。
 *
 * SlotValue (proto) と KomaSlots (frontend 編集状態) の変換、
 * BG 全体合計のライブ算出を担当する。
 */
import { create } from '@bufbuild/protobuf'
import type {
  PastActual,
  SlotValue,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { SlotValueSchema } from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { decimalToNumber } from '~/models/decimal'
import { komaNumberToTime } from '~/models/komaToTime'
import { formatKwh } from '~/models/kwhDisplay'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

/** number | undefined → proto Decimal | undefined */
function numberToDecimal(n: number | undefined): Decimal | undefined {
  if (n === undefined) return undefined
  return create(DecimalSchema, { value: numberToPlainString(n) })
}

/**
 * number → 指数表記を使わない 10 進文字列。`String(1e-7)` は '1e-7' になり、
 * backend の Decimal 文字列検証が拒否するため、指数表記になる値だけ
 * 仮数の桁を落とさずに小数点を移して通常表記へ展開する
 * (固定の小数桁数で整形すると 1e-21 のような値が '0' になる)。
 */
export function numberToPlainString(n: number): string {
  const s = String(n)
  const match = /^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/.exec(s)
  if (match === null) return s
  const [, sign, intPart, fracPart = '', expText] = match
  const digits = intPart + fracPart
  const pointPos = intPart.length + Number(expText)
  if (pointPos <= 0) return `${sign}0.${'0'.repeat(-pointPos)}${digits}`
  if (pointPos >= digits.length) {
    return `${sign}${digits}${'0'.repeat(pointPos - digits.length)}`
  }
  return `${sign}${digits.slice(0, pointPos)}.${digits.slice(pointPos)}`
}

/**
 * backend の Decimal 文字列の長さ上限 (go/internal/backend/adapter/grpc/converter/decimal.go
 * の maxDecimalLen)。超える値は保存時に InvalidArgument で拒否され、下書き全体が保存できない。
 */
const MAX_DECIMAL_STRING_LENGTH = 64

/**
 * 需要想定値として保存できる数値か。有限で、指数表記を使わない文字列にした時に
 * backend の長さ上限に収まるものだけを受け付ける (1e-50 のような極小値は展開後に上限を超える)。
 */
export function isSavableSlotValue(n: number): boolean {
  return (
    Number.isFinite(n) &&
    numberToPlainString(n).length <= MAX_DECIMAL_STRING_LENGTH
  )
}

/**
 * 2 進浮動小数の演算誤差 (例: 27 * (13/6) = 58.49999999999999) だけを吸収する。
 * 有効 15 桁に揃えるのみで、業務上の丸め (整数化等) ではない
 * (AC-DFC-EDIT-003: 需要想定値は丸めず保持する)。
 */
export function normalizeFloat(n: number): number {
  return Number(n.toPrecision(15))
}

/** AC-DFD-006a: チャート前端の前日延長コマ数 (前日 23:00 / 23:30)。 */
export const DAY_BOUNDARY_PRE_EXTENSION_KOMAS = 2

/** AC-DFD-006a: チャート後端の延長点数 (24:00 から 27:00 まで = 7 点)。 */
export const DAY_BOUNDARY_POST_EXTENSION_KOMAS = 7

/**
 * ForecastChart の X 軸の点数 (前日 23:00〜翌日 3:00 = 57 点)。範囲は対象日と
 * 今日との前後関係によらず同一 (AC-DFD-006a)。気温・露点系列の長さ (48 / 54) には
 * 依存しない。
 */
export const CHART_SLOT_COUNT =
  DAY_BOUNDARY_PRE_EXTENSION_KOMAS + 48 + DAY_BOUNDARY_POST_EXTENSION_KOMAS

/**
 * ForecastChart が実際に描画する過去実績系列の最大件数 (spec AC-001/AC-005:
 * demand-forecast-daily-spec-align で 3→5 に拡大)。ForecastChart の描画
 * スライスと hasChartTemperatureData の判定スライスが同じ値を参照すること
 * (値の二重定義をしない)。
 */
export const MAX_CHART_PAST_ACTUALS = 5

/**
 * ForecastChart の右 YAxis (気温・露点) を実際に描くかの気温存在述語。
 * ForecastCard (WeatherBar の showRightAxis 配布) と ForecastChart (右 YAxis
 * 描画) の両方が本関数を同一入力で呼ぶことで、判定の二重導出によるズレ
 * (= 6 件目以降の pastActual にのみ気温がある場合に真偽が割れ、天気帯右端が
 * 40px ズレるバグ) を防ぐ。pastActuals は ForecastChart が実際に描画する上限
 * (MAX_CHART_PAST_ACTUALS) でスライスしてから判定する — 「実際に描画される
 * 系列に気温があるか」で右軸表示を判定するのが正しいため、6 件目以降にのみ
 * 気温がある edge では false (右軸なし) に寄せる。
 *
 * 対象日系列は「折れ線として描けるか」で判定する。単点 (または離散点) の系列は
 * 線が一切描けない (dot=false) のに軸・凡例・天気帯右軸だけ残るため
 * (PR #6250 後の翌々日断面で予測気温が日境界 1 点だけ残った残存バグ)。判定述語は
 * 系列の `connectNulls` 設定で使い分ける:
 * - 予測気温 (tempForecast, `connectNulls`) → `hasDrawableSlotValue`
 *   (欠測を跨いで結ぶため有効点 2 点以上で描ける)
 * - 実績気温・露点 (tempActual / dewActual, `connectNulls={false}`) →
 *   `hasConsecutiveDrawableSlotValue` (隣接する有効点ペアが無いと線分が描けない、
 *   PR #6399 codex review 指摘)
 * 過去日系列は現実には全 48 コマの実績で届き (実績未到着は全欠測配列)、単点で
 * 届くことが無いため従来どおり `hasDefinedProtoSlotValue` (存在判定) のままとする
 * (AC-05/AC-23 の描画契約を変えない)。
 */
export function hasChartTemperatureData(params: {
  temperatureForecastSlots?: KomaSlots
  temperatureActualSlots?: KomaSlots
  dewpointActualSlots?: KomaSlots
  pastActuals: PastActual[]
}): boolean {
  return (
    hasDrawableSlotValue(params.temperatureForecastSlots) ||
    hasConsecutiveDrawableSlotValue(params.temperatureActualSlots) ||
    hasConsecutiveDrawableSlotValue(params.dewpointActualSlots) ||
    params.pastActuals
      .slice(0, MAX_CHART_PAST_ACTUALS)
      .some(
        (pa) =>
          hasDefinedProtoSlotValue(pa.temperatureSlots) ||
          hasDefinedProtoSlotValue(pa.dewpointSlots),
      )
  )
}

/**
 * 露点データの有無を判定する共有述語 (AC-05/AC-06/AC-11)。対象日の実績露点に
 * 加え、選択過去日の露点実績 (pastActuals[].dewpointSlots) も参照する —
 * 「対象日に露点が無く過去日にのみ露点がある場合」も第2軸・凡例・露点トグル
 * を出す必要があるため (design §4 主要意思決定)。予測露点は spec #4290 改訂
 * (2026-08-14) で撤去済み (JMA 公開予報が湿度を配信しないため常に空スロット
 * だった)。ForecastCard (凡例構築) と ForecastChart (露点 Line 描画可否) の
 * 両方が本関数を同一入力で呼ぶことで判定の二重導出によるズレを防ぐ
 * (hasChartTemperatureData と同じ原則)。pastActuals は実際に描画される上限
 * (MAX_CHART_PAST_ACTUALS) で本関数内部でスライスしてから判定する
 * (hasChartTemperatureData と同じく、呼び出し側のスライス有無に依存しない —
 * 「実際に描画される系列に露点があるか」で判定するのが正しいため)。対象日の
 * 露点実績 (dewActual, `connectNulls={false}`) は `hasConsecutiveDrawableSlotValue`
 * (隣接する有効点ペアが無いと線分が描けない) で判定する (離散点は折れ線として
 * 描けないため軸・凡例を出さない、hasChartTemperatureData JSDoc 参照)。
 */
export function hasDewpointData(params: {
  dewpointActualSlots?: KomaSlots
  pastActuals: PastActual[]
}): boolean {
  return (
    hasConsecutiveDrawableSlotValue(params.dewpointActualSlots) ||
    params.pastActuals
      .slice(0, MAX_CHART_PAST_ACTUALS)
      .some((pa) => hasDefinedProtoSlotValue(pa.dewpointSlots))
  )
}

/**
 * proto SlotValue[] を KomaSlots に変換する。
 * 配列長が `length` に満たない場合は undefined で埋める。
 *
 * `length` 既定 48 (= 需要想定値・過去実績・確定履歴比較等の通常スロット系列)。
 * 気温・露点の系列 (AC-DFD-006a-2 の延長部を含む) では呼び出し側が 54 まで指定できる。
 *
 * 値の変換はアプリ横断の `~/models/decimal` の `decimalToNumber` に委譲する
 * (= 非数値・非有限値〈"Infinity" / "1e400" 等〉は欠測 undefined に畳む)。ここは
 * スロット値の入口なので、弾き漏らすと下流全体 (軸 domain 算出・最大電力ラベル・
 * チャートドラッグ確定値・KomaGrid 表示・保存 request) へ流れる。ただし基準は
 * Number.isFinite なので "1.7e308" のような**有限の極大値は通過する** —
 * その先の overflow は `models/chartAxis.ts` 側のガードが受け持つ。
 */
export function protoSlotsToKoma(slots: SlotValue[], length = 48): KomaSlots {
  const result: KomaSlots = new Array<number | undefined>(length).fill(
    undefined,
  )
  for (let i = 0; i < slots.length && i < length; i++) {
    const sv = slots[i]
    if (sv !== undefined) {
      result[i] = sv.value !== undefined ? decimalToNumber(sv.value) : undefined
    }
  }
  return result
}

/**
 * KomaSlots を proto SlotValue[] (48 個) に変換する。
 * undefined スロットは value なしの SlotValue として送る (= 空セル)。
 */
export function komaToProtoSlots(koma: KomaSlots): SlotValue[] {
  const padded: KomaSlots =
    koma.length < 48
      ? [
          ...koma,
          ...new Array<number | undefined>(48 - koma.length).fill(undefined),
        ]
      : koma.slice(0, 48)
  return padded.map((v) =>
    create(SlotValueSchema, {
      value: numberToDecimal(v),
    }),
  )
}

/**
 * BG合計の内訳系列。帰属不明の0/未入力は合計へ寄与せず、全小売未入力を
 * 0表示へ変えない。表示・baseline・保存検査で同じ内訳を使う。
 */
export function bgTotalBreakdownSlots(
  retailerSlots: KomaSlots[],
  unattributedSlots: KomaSlots | undefined,
): KomaSlots[] {
  return unattributedSlots === undefined
    ? retailerSlots
    : [
        ...retailerSlots,
        unattributedSlots.map((value) => (value === 0 ? undefined : value)),
      ]
}

/**
 * 複数小売の forecastSlots を合算した BG 全体合計を計算する (編集中のライブ
 * プレビュー用)。全小売で undefined のコマは undefined に、1 つでも値があれば
 * 加算する (= present 0 は和に参加し、空セル undefined とは区別する)。
 *
 * None/0 規則の正本 (SoT) は backend の entity.SumSlots
 * (go/internal/backend/domain/entity/slot_aggregate.go)。本関数はその規則を
 * frontend のライブ算出向けにミラーしたもので、保存・確定の確定値は常に backend
 * 側で再計算される。両者の None/0 挙動がズレないこと。
 */
export function calcBgTotalSlots(retailerSlots: KomaSlots[]): KomaSlots {
  const result: KomaSlots = new Array<number | undefined>(48).fill(undefined)
  for (let i = 0; i < 48; i++) {
    let hasValue = false
    let sum = 0
    for (const slots of retailerSlots) {
      const v = slots[i]
      if (v !== undefined) {
        hasValue = true
        sum += v
      }
    }
    result[i] = hasValue ? sum : undefined
  }
  return result
}

/**
 * コマ番号 (1-indexed, 1〜54) → 時刻文字列、AC-006a 54スロットチャート専用。
 * 1〜48 は `komaNumberToTime` (`~/models/komaToTime`) と同じ (対象日の時刻)。
 * 49〜54 は 24 時間で折り返し、翌日 (D+1) の 00:00 から 30 分刻みのパターンを
 * 継続する (コマ49=00:00, コマ50=00:30, ..., コマ54=02:30)。GcCountdown 等が
 * 使う「コマ49=24:00 (当日終端)」表現 (`komaNumberToTime` の契約) とは文脈が
 * 異なるため分離する (チャート X 軸ラベル専用の adapter)。
 */
export function extendedKomaToTimeLabel(koma: number): string {
  return komaNumberToTime(((koma - 1) % KOMA_COUNT) + 1)
}

/**
 * KomaSlots の指定コマを更新したコピーを返す。
 * koma は 1-indexed。
 */
export function updateKomaSlot(
  slots: KomaSlots,
  koma: number,
  value: number | undefined,
): KomaSlots {
  const next = [...slots]
  next[koma - 1] = value
  return next
}

/**
 * 表示用の数値フォーマット (AC-DFC-EDIT-003: 保持値は小数のまま、画面表示だけ
 * 整数)。undefined は空文字、0 は "0"、小数は小数第 1 位を四捨五入 (half-away-
 * from-zero) した整数を 3 桁区切りで返す。保持値は変えない。
 */
export function formatSlotValue(v: number | undefined): string {
  if (v === undefined) return ''
  return formatKwh(v)
}

/**
 * セル編集開始時の入力欄テキスト (AC-DFC-EDIT-003)。保持値そのものを丸めず、
 * 桁区切りなしで返す。表示用の `formatSlotValue` と分け、変更せず blur した
 * no-op 編集で保持値が丸められないようにする。
 */
export function slotValueToEditText(v: number | undefined): string {
  return v === undefined ? '' : numberToPlainString(v)
}

/**
 * 48コマ中のピークコマ (番号 + 値) を探す共通 helper。
 * `computePeak` (数値のみ) と `computePeakLabel` (koma + 表示ラベル、
 * spec AC-005c) の両方がこの探索ロジックを共有する。
 * GCロック済み (koma <= gcKoma) のコマは対象から除外する。
 *
 * demand-fix-48-koma (kWh 整数統一) で瞬時電力換算 (× 2) を廃止した
 * (旧 `findPeakKomaMw`)。30 分値スロットの max をそのまま「ピーク需要」とする
 * (spec AC-DFC-011: 48 コマ値・チャート Y 軸・ピーク需要は同一 kWh スケールで揃える)。
 */
function findPeakKoma(
  slots: KomaSlots,
  gcKoma = 0,
): { koma: number; value: number } | undefined {
  let best: { koma: number; value: number } | undefined
  for (let i = 0; i < slots.length; i++) {
    const v = slots[i]
    if (v === undefined) continue
    const koma = i + 1
    if (gcKoma > 0 && koma <= gcKoma) continue
    if (best === undefined || v > best.value) best = { koma, value: v }
  }
  return best
}

/**
 * 48コマの現在ピーク需要 (内部単位、送電端 kWh/30min) を算出する
 * (「最大電力補正」モーダルの「現在の最大電力」表示 / rescale 基準値、
 * design L873-896 `_dftRescaleCurrentMax`)。GCロック済み (koma <= gcKoma) の
 * コマは基準ピーク計算から除外する (design の `_dftRescaleCurrentMax` と同様、
 * 受給済みコマは対象外)。
 *
 * demand-fix-48-koma (kWh 整数統一、旧 `computePeakMw`) で瞬時電力換算 (× 2) を
 * 廃止した。内部値がそのまま kWh 表示値なので、呼び出し側 (RescaleModal 表示)
 * は単位変換を挟まず千区切り整形だけを行う。
 */
export function computePeak(slots: KomaSlots, gcKoma = 0): number {
  return findPeakKoma(slots, gcKoma)?.value ?? 0
}

/** `computePeakLabel` の結果。 */
export type PeakLabel = { koma: number; kwhLabel: string }

/**
 * 需要想定値チャートの最大電力ライン注釈 (spec AC-005c、demand-forecast-
 * spec-compliance AC-03/04) 用に、ピークコマ番号と kWh 表示 (千区切り) 済み
 * ラベルを算出する。既存 `computePeak` と同じ `findPeakKoma` を再利用・拡張
 * したもの。関数側で `formatKwh` (小数第 1 位を四捨五入した整数) まで済ませるため、呼び出し側は
 * 追加の整形を挟まない (demand-fix-48-koma で旧 `computePeakMwLabel` の × 2
 * 換算を廃し kWh 表示に置き換え)。
 * 全コマ空 (= ピークが無い) の場合は undefined を返し、呼び出し側
 * (ForecastChart) は最大電力ラインを非表示にする。
 */
export function computePeakLabel(
  slots: KomaSlots,
  gcKoma = 0,
): PeakLabel | undefined {
  const peak = findPeakKoma(slots, gcKoma)
  if (peak === undefined) return undefined
  return {
    koma: peak.koma,
    kwhLabel: formatKwh(peak.value),
  }
}

/**
 * 「最大電力補正」(design L570 / L873-896): 現在の48点ピークが
 * targetPeakMw (内部単位、送電端 kWh/30min) になるよう全コマを比例配分
 * する。基準ピーク算出・比例配分適用のいずれも GCロック済み (koma <= gcKoma)
 * のコマは対象外 (design L889 「受給済みコマは変更しません」)。
 * ピークが 0 (編集可能コマが全て空/0) の場合は比例配分できないため変更しない。
 *
 * AC-DFD-006 / AC-DFC-EDIT-001 / AC-DFC-EDIT-003 に従い、補正後の各コマの値
 * (送電端 kWh/30min。`KomaSlots` docstring 参照) は丸めず小数のまま保持する
 * (表示だけが整数)。
 */
export function rescaleSlotsToPeak(
  slots: KomaSlots,
  targetPeakMw: number,
  gcKoma: number,
): KomaSlots {
  const currentPeak = computePeak(slots, gcKoma)
  if (currentPeak <= 0) return slots
  const factor = targetPeakMw / currentPeak
  return slots.map((v, i) => {
    const koma = i + 1
    if (v === undefined) return v
    if (gcKoma > 0 && koma <= gcKoma) return v
    return normalizeFloat(v * factor)
  })
}

/**
 * 補正モード「編集コマ以降へ比例配分」(design forecast-today.html L2255-2266
 * `_dftApplyProportional` 準拠)。編集コマ (editedKoma) を newValue に更新し、それ以降の
 * コマ (koma > editedKoma) を「確定値 (baselineSlots) の比率 (newValue ÷
 * baselineSlots[editedKoma])」で上書きする。GCロック済み (koma <= gcKoma) のコマ、
 * および baselineSlots が未定義のコマは対象外。
 *
 * baselineSlots[editedKoma] が未定義または 0 の場合は比率を算出できないため、
 * editedKoma 自体の更新のみ行い、それ以降のコマは変更しない
 * (design の `if (!orig || orig === 0) return;` 準拠)。
 *
 * セル編集 (KomaGrid commit) とチャートドラッグ編集の両経路から呼ばれる共通ロジック。
 *
 * AC-DFD-006 / AC-DFC-EDIT-003 に従い、比例配分後の各コマの値 (送電端
 * kWh/30min) は丸めず小数のまま保持する (`rescaleSlotsToPeak` と同じ方針。
 * 表示だけが整数)。
 */
export function applyProportional(
  baselineSlots: KomaSlots,
  currentSlots: KomaSlots,
  editedKoma: number,
  newValue: number,
  gcKoma: number,
): KomaSlots {
  const next = updateKomaSlot(currentSlots, editedKoma, newValue)
  const orig = baselineSlots[editedKoma - 1]
  if (orig === undefined || orig === 0) return next

  const ratio = newValue / orig
  const length = Math.min(baselineSlots.length, next.length)
  for (let koma = editedKoma + 1; koma <= length; koma++) {
    if (gcKoma > 0 && koma <= gcKoma) continue
    const origR = baselineSlots[koma - 1]
    if (origR === undefined) continue
    next[koma - 1] = normalizeFloat(origR * ratio)
  }
  return next
}

/**
 * 参照列 (過去実績／確定履歴) の「採用」(spec AC-DFD-047/047a/047b, AC-DFD-048/048a):
 * 参照列の値 (reference) を現在の需要想定値 (current) へコピーする。
 *
 * - 空コマ (reference が undefined) は current の値を維持する (AC-DFD-004 の
 *   空セル/0 の視覚区別を崩さないため。旧 `handleAdoptPastActual` と同じ規律)。
 * - GC 済みコマ (`koma <= gcKoma`) は current を維持し、上書きしない (AC-DFD-035a)。
 * - reference が 0 の場合は有効値としてコピーする (空/0 を区別する)。
 * - コピー値は丸めず参照値をそのまま採用する (AC-DFC-EDIT-003: 需要想定値は
 *   小数のまま保持し、表示だけが整数)。過去実績参照列は託送実績の送電端換算値
 *   で小数を持つが、その精度のまま入れる。
 */
export function adoptReferenceColumnSlots(
  current: KomaSlots,
  reference: KomaSlots,
  gcKoma: number,
): KomaSlots {
  return current.map((v, i) => {
    const koma = i + 1
    if (gcKoma > 0 && koma <= gcKoma) return v
    const refValue = reference[i]
    return refValue === undefined ? v : refValue
  })
}

/**
 * GCロック外の全コマ番号 (1-indexed) を manual 化対象として返す。
 * 「最大電力で揃える」や「トレンド反映」など、編集可能コマを一括で manual 扱い
 * にする操作で使う。gcKoma <= 0 の場合は全 48 コマを返す。
 */
export function markEditableKomasManual(gcKoma: number): Set<number> {
  const manual = new Set<number>()
  for (let k = 1; k <= 48; k++) {
    if (gcKoma <= 0 || k > gcKoma) manual.add(k)
  }
  return manual
}

/** KomaGrid「差」列 (design L2423-2441) の算出結果。 */
export type SlotDiff = {
  /** 内部単位 (送電端 kWh/30min) の生差分 (変更案 - 確定値) */
  delta: number
  /** 確定値に対する変化率 (%)。確定値が 0 の場合は算出不能のため null */
  pct: number | null
}

/**
 * slots 配列に 1 つでも値が定義されているか (= 系列/列を描画すべきか) を判定する。
 * ForecastChart (Line/Area 描画可否) と chartLegend (凡例表示可否) の両方で同じ
 * 判定を重複させないための共通 helper。
 */
export function hasDefinedSlotValue(slots: KomaSlots | undefined): boolean {
  return slots !== undefined && slots.some((v) => v !== undefined)
}

/**
 * proto SlotValue[] に 1 つでも値が定義されているかを判定する
 * (`hasDefinedSlotValue` の proto 入力版)。
 *
 * 配列長で判定してはいけない: backend の DemandActualProvider 契約では「実績
 * 未到着」も **None 要素を持つ 48 件の非 nil 配列** で返る (= 実績なしと nil
 * 省略は別物、`domain/provider/demand_actual_provider.go` godoc)。長さだけを見ると
 * 全コマ欠測の過去日を「系列あり」と誤判定し、値を一点も描けない空の Line・
 * 第2軸・凡例が出る。
 */
export function hasDefinedProtoSlotValue(slots: SlotValue[]): boolean {
  return hasDefinedSlotValue(protoSlotsToKoma(slots, slots.length))
}

/**
 * slots に「折れ線として描画できる」有効点が 2 点以上あるかを判定する
 * (`connectNulls` = null を跨いで結ぶ系列向け)。
 *
 * 予測気温 (tempForecast) は `connectNulls` の破線で描くため、欠測を跨いでも
 * 有効点が 2 点あれば線が繋がる。有効点が 1 点だけだと線分にならず一切描画
 * されない (dot=false なので点も出ない)。それでも「1 点でも値があれば表示」で
 * 右 YAxis・凡例・天気帯右軸を出すと、線は無いのに「気温 (℃)」の軸・凡例だけが
 * 残る (PR #6250 で予測気温の後端 back-hold を撤去した後、翌々日断面で日境界
 * 1 点だけが残ったケース)。軸・凡例の gate は「値があるか」ではなく「折れ線と
 * して描けるか」で判定する。
 *
 * `connectNulls={false}` の系列 (対象日実績気温・露点) は隣接する有効点が無いと
 * 線分が描けないため、本述語ではなく `hasConsecutiveDrawableSlotValue` を使う。
 */
export function hasDrawableSlotValue(slots: KomaSlots | undefined): boolean {
  if (slots === undefined) return false
  let count = 0
  for (const v of slots) {
    if (v !== undefined) {
      count += 1
      if (count >= 2) return true
    }
  }
  return false
}

/**
 * slots に「`connectNulls={false}` の折れ線として描画できる」= 隣接する有効点
 * ペアが 1 組以上あるかを判定する。
 *
 * 対象日実績気温 (tempActual) と実績露点 (dewActual) の Line は
 * `connectNulls={false}` かつ `dot={false}` のため、欠測を跨いだ離散点
 * (例 `[20, undefined, 22]`) では線分が一切描画されない (隣り合うコマが両方
 * 有効な区間だけが線になる)。個数だけを見る `hasDrawableSlotValue` ではこの
 * 離散点を描画可能と誤判定し、線は無いのに右 YAxis・凡例・天気帯右軸だけが
 * 残ってしまう (PR #6399 codex review 指摘)。隣接ペアの有無で描画可否を判定する。
 */
export function hasConsecutiveDrawableSlotValue(
  slots: KomaSlots | undefined,
): boolean {
  if (slots === undefined) return false
  for (let i = 0; i + 1 < slots.length; i++) {
    if (slots[i] !== undefined && slots[i + 1] !== undefined) return true
  }
  return false
}

/** 48コマ中の最大値/最小値とそのコマ番号 (1-indexed)。 */
export type SlotExtremum = { value: number; koma: number }

/**
 * forecastSlots (KomaSlots) の最大値/最小値とそのコマ番号を算出する
 * (design forecast-today.html [2026-06-16] 最小/最大/ピーク 指標、L3225-3255)。
 * ForecastChart の水平 ReferenceLine 注釈に使う。全コマ undefined (未入力、翌々日
 * 初期状態等) の場合は max/min とも undefined を返す (= 呼び出し側はアノテーション
 * 非表示)。
 */
export function computeSlotExtrema(slots: KomaSlots): {
  max?: SlotExtremum
  min?: SlotExtremum
} {
  let max: SlotExtremum | undefined
  let min: SlotExtremum | undefined
  for (let i = 0; i < slots.length; i++) {
    const v = slots[i]
    if (v === undefined) continue
    const koma = i + 1
    if (max === undefined || v > max.value) max = { value: v, koma }
    if (min === undefined || v < min.value) min = { value: v, koma }
  }
  return { max, min }
}

/**
 * 「差」列 (design L2423-2441): 変更案 (current) と確定値 (baseline) の差分を算出する。
 * どちらかが未定義 (= 未確定 / 未編集) の場合は算出不可のため undefined を返す
 * (呼び出し側は「—」を表示する)。
 */
export function computeSlotDiff(
  baseline: number | undefined,
  current: number | undefined,
): SlotDiff | undefined {
  if (baseline === undefined || current === undefined) return undefined
  const delta = current - baseline
  const pct = baseline !== 0 ? (delta / baseline) * 100 : null
  return { delta, pct }
}

/**
 * 「実績との差分」列 (KomaGrid ActualDiffCell、spec AC-10) の表示・コピー
 * 共通丸め: native `Math.round` は半分を +∞ 方向へ丸める (`-0.5 → -0`) ため
 * 符号を先に取り出し、絶対値を丸めてから符号を戻す。
 *
 * epsilon 補正は持たない。KomaGrid の既存挙動をそのまま抽出したもの
 * (2026-09-04 frontend-test 削減第3段で KomaGrid.tsx の表示 (ActualDiffCell)
 * とコピー (Ctrl+C TSV) の重複実装をここへ統合)。
 */
export function roundSignedDelta(delta: number): number {
  return Math.sign(delta) * Math.round(Math.abs(delta))
}
